import { ReactNode, useEffect, useRef, useState } from 'react';

interface ILazyLoadWrapperProps {
  children: ReactNode;
  /** Vertical offset in pixels to trigger loading before element is in viewport */
  offset?: number;
  /** Height of the placeholder while content is not loaded */
  height?: number | string;
  /** Custom placeholder element */
  placeholder?: ReactNode;
  /** Only load once (don't unload when scrolling away) */
  once?: boolean;
  /** Additional className for the wrapper */
  className?: string;
  /** Additional style for the wrapper */
  style?: React.CSSProperties;
  /** ID for the wrapper element */
  id?: string;
}

/**
 * LazyLoadWrapper - A React 19 compatible replacement for react-lazyload
 * Uses IntersectionObserver to lazily load content when it enters the viewport.
 */
export function LazyLoadWrapper({
  children,
  offset = 0,
  height,
  placeholder = <div />,
  once = true,
  className,
  style,
  id
}: ILazyLoadWrapperProps) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }

    // If already visible and once=true, no need to observe
    if (isVisible && once) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            if (once) {
              observer.unobserve(element);
            }
          } else if (!once) {
            setIsVisible(false);
          }
        });
      },
      {
        rootMargin: `${offset}px 0px ${offset}px 0px`
      }
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [offset, once]);

  const wrapperStyle: React.CSSProperties = {
    ...style,
    ...(height && !isVisible ? { minHeight: height } : {})
  };

  return (
    <div ref={ref} className={className} style={wrapperStyle} id={id}>
      {isVisible ? children : placeholder}
    </div>
  );
}

export default LazyLoadWrapper;
